import React, {ReactElement, useEffect} from "react";
import {Link} from "react-router-dom";
import {ReactComponent as PlusIcon} from "components/Icons/plus.svg";
import {ReactComponent as PlusEmptyIcon} from "components/Icons/plusEmpty.svg";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuManagementActions, menuManagementSelectors} from "./menuManagementSlice";
import {authActions, authSelectors} from "features/auth/authSlice";
import {FormattedMessage} from "react-intl";
import {Spinner} from "components/UIComponents/Spinner";
import {PageTitle} from "components/UIComponents/PageTitle";
import {MenuList} from "./MenuList";
import {RenameModal} from "./RenameModal";
import {DeleteModal} from "./DeleteModal";
import {Navigation} from "components/UIComponents/Navigation";
import {IconLabel} from "components/UIComponents/IconLabel";
import {PrimaryButton} from "components/UIComponents/PrimaryButton";
import {SecondaryButton} from "../../../components/UIComponents/SecondaryButton";
import {LanguageDropdown} from "../../i18n/LanguageDropdown";
import {i18nActions, i18nSelectors} from "../../i18n/i18nSlice";
import {Language} from "../../i18n/translations";
import {CopyModal} from "./CopyModal";
import {CopyModalNotification} from "./CopyModalNotification";
import {Menu} from "@headlessui/react";
import { flatten, groupBy } from "ramda";

export interface MenuManagementProps {

}

export function MenuManagement(props: MenuManagementProps): ReactElement {
    const dispatch = useAppDispatch();
    const appLanguage = useAppSelector(i18nSelectors.language);
    const userId = useAppSelector(authSelectors.customer);
    const isSalesUser = useAppSelector(authSelectors.isSalesUser);
    const menusList = useAppSelector(menuManagementSelectors.menus(userId));
    const loading = useAppSelector(menuManagementSelectors.loading(userId));

    const menus = groupBy(e=>(e as any).type, menusList);

    useEffect(() => {
        if (userId != null) {
            dispatch(menuManagementActions.fetchMenusIfNecessary(userId));
        }
    }, [dispatch, userId]);

    const setAppLanguage = (lang: Language) => dispatch(i18nActions.setLanguage(lang));

    const unsetCustomer = () => {
        dispatch(authActions.setCustomer(null))
    }

    useEffect(()=>{
        if (!menusList || menusList.length===0) return;
    }, [menusList]);

    return (
        <>
            <CopyModal />
            <RenameModal />
            <DeleteModal />
            <div id="menu-management" className="flex flex-col items-stretch h-screen w-screen">
                <div className="flex-none">
                    <Navigation
                        left={
                        <>
                            <PageTitle><FormattedMessage id="management.title" values={{customer: userId}}/></PageTitle>
                            {isSalesUser && (
                                <SecondaryButton onClick={unsetCustomer}>
                                    <FormattedMessage id="management.choseCustomer"/>
                                </SecondaryButton>
                            )}
                            <div className="max-w-28">
                                <LanguageDropdown
                                    buttonClassName="rounded-full border border-gray-1
                                                     pl-5 px-2 py-2
                                                     text-primary font-barlow font-semibold leading-none"
                                    value={appLanguage}
                                    onChangeValue={setAppLanguage}
                                />
                            </div>
                        </>
                        }
                        right={
                            <Menu as="div" className="relative">
                                <Menu.Button>
                                    <PrimaryButton>
                                        <IconLabel icon={<PlusIcon className="inline-block" />}>
                                            <FormattedMessage id="management.create"/>
                                        </IconLabel>
                                    </PrimaryButton>
                                </Menu.Button>
                                <Menu.Items className="absolute right-0 origin-bottom-right mt-2
                                                   rounded-md drop-shadow-dropdown-menu bg-white
                                                   font-barlow text-base py-4 z-popover">
                                    {[
                                        {link: "create/wine", translationId: "mainpage.create.label.weinkarte"},
                                        {link: "create/leaflet", translationId: "mainpage.create.label.leaflet"},
                                        ...(isSalesUser?[{link: "create/label", translationId: "mainpage.create.label.label"}]:[]),
                                    ].map(({link, translationId}) => (
                                        <Menu.Item key={translationId}>
                                            <Link to={link}>
                                                <button className="flex items-center gap-3 px-4 py-2 w-full hover:bg-warm-gray">
                                                    <span className="flex-none text-primary"><PlusEmptyIcon className="w-5 h-5" /></span>
                                                    <span className="flex-grow text-left"><FormattedMessage id={translationId} /></span>
                                                </button>
                                            </Link>
                                        </Menu.Item>
                                    ))}
                                </Menu.Items>
                            </Menu>
                        }
                    />
                </div>

                <CopyModalNotification/>

                <div className="flex-auto flex flex-col overflow-y-auto bg-warm-gray w-full h-full">
                    {loading ? (
                        <Spinner className="w-10 h-10 m-auto" />
                    ) : (
                        <>
                            {flatten(Object.values(menus)).length === 0 
                                ? <EmptyList />
                                : (
                                    <>
                                        {([
                                            ["wine", "mainpage.sections.myWeinkarts"],
                                            ["leaflet", "mainpage.sections.myLeaflets"],
                                            ["label", "mainpage.sections.myLabels"],
                                        ]).map(([type, translationKey]) => {
                                            const m = menus[type] || [];
                                            if (m.length === 0) return null;
                                            return <React.Fragment key={type}>
                                                <div className="pt-16 px-12 font-bold text-xl">
                                                    <FormattedMessage id={translationKey} />
                                                </div>
                                                <MenuList menus={m} />
                                            </React.Fragment>
                                        })}
                                    </>
                                )
                            }
                        </>
                    )}
                </div>


            </div>
        </>
    );
}

interface EmptyListProps {

}

function EmptyList(props: EmptyListProps): ReactElement {
    return <div className="text-center"><FormattedMessage id="management.noMenus"/></div>
}
